import type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
  GetQuickFilterTextParams,
} from 'ag-grid-community'
import type { TaxGridRow } from '../../../hooks/useMatchTaxData'
import { formatVolume, formatVolumeConsistent, formatPerUnitConsistent, formatCurrencyConsistent } from '../formatters'
import type { ColumnPrecision } from '../../utils/precisionCalculator'

// Interface for dynamic currency formatters
export interface TaxCurrencyFormatters {
  formatCurrency: (amount?: number) => string
  formatPerUnit: (amount?: number) => string
  currencySymbol: string
}

export function createTaxColumnDefs(
  currencyFormatters?: TaxCurrencyFormatters,
  precisions?: ColumnPrecision
): ColDef<TaxGridRow>[] {
  // Use provided precisions or fallback to defaults (tax rates typically need fewer decimals)
  const columnPrecisions = precisions || { volume: 3, perUnit: 4, currency: 2 }

  // Fallback to deprecated formatters if none provided
  const { formatCurrency, formatPerUnit, currencySymbol } = currencyFormatters || {
    formatCurrency: (amount?: number) => {
      if (amount === null || amount === undefined) return '$0.00'
      if (amount === 0) return '$0.00'
      const sign = amount < 0 ? '-' : ''
      return `${sign}$${Math.abs(amount).toFixed(2)}`
    },
    formatPerUnit: (amount?: number) => {
      if (amount === null || amount === undefined) return '$0.0000'
      if (amount === 0) return '$0.0000'
      const sign = amount < 0 ? '-' : ''
      return `${sign}$${Math.abs(amount).toFixed(4)}`
    },
    currencySymbol: '$',
  }

  // Currency cell renderer for tax totals (with consistent precision)
  const TaxTotalCurrencyRenderer = ({ value }: ICellRendererParams<TaxGridRow>) => {
    if (value === null || value === undefined) {
      return formatCurrencyConsistent(0, columnPrecisions.currency, currencySymbol)
    }
    return formatCurrencyConsistent(value, columnPrecisions.currency, currencySymbol)
  }

  // Currency cell renderer for per unit (with consistent precision)
  const TaxPerUnitCurrencyRenderer = ({ value }: ICellRendererParams<TaxGridRow>) => {
    if (value === null || value === undefined) {
      return formatPerUnitConsistent(0, columnPrecisions.perUnit, currencySymbol)
    }
    return formatPerUnitConsistent(value, columnPrecisions.perUnit, currencySymbol)
  }

  return [
    {
      headerName: 'TAX DESCRIPTION',
      flex: 1,
      sortable: false,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      rowDrag: true,
      rowDragText: (params) => {
        return params.rowNode?.data?.tax_description || ''
      },
      valueGetter: (params: ValueGetterParams<TaxGridRow>) => {
        return params.data?.tax_description || ''
      },
      cellRenderer: (params: ICellRendererParams<TaxGridRow>) => {
        return params.value || 'N/A'
      },
    },
    {
      headerName: 'PRODUCT',
      flex: 0.5,
      sortable: false,
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      valueGetter: (params: ValueGetterParams<TaxGridRow>) => {
        return params.data?.product_name || 'N/A'
      },
      cellRenderer: (params: ICellRendererParams<TaxGridRow>) => {
        return params.value || 'N/A'
      },
    },
    {
      headerName: 'TAX TYPE',
      flex: 0.5,
      sortable: false,
      hide: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      valueGetter: (params: ValueGetterParams<TaxGridRow>) => {
        return params.data?.type || ''
      },
    },
    {
      headerName: 'TAX AUTHORITY',
      flex: 0.8,
      sortable: false,
      hide: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      valueGetter: (params: ValueGetterParams<TaxGridRow>) => {
        return params.data?.tax_authority || ''
      },
    },
    {
      headerName: 'VOLUME',
      flex: 0.3,
      sortable: false,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      valueGetter: (params: ValueGetterParams<TaxGridRow>) => {
        return params.data?.amount ?? null
      },
      valueFormatter: (params: ValueFormatterParams<TaxGridRow>) => {
        return params.value !== null && params.value !== undefined
          ? formatVolumeConsistent(params.value, columnPrecisions.volume)
          : 'N/A'
      },
    },
    {
      headerName: 'PER UNIT',
      flex: 0.4,
      sortable: false,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      valueGetter: (params: ValueGetterParams<TaxGridRow>) => {
        return params.data?.rate ?? null
      },
      cellRenderer: TaxPerUnitCurrencyRenderer,
    },
    {
      headerName: `TOTAL (${currencySymbol})`,
      flex: 0.4,
      type: 'numericColumn',
      sortable: false,
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      valueGetter: (params: ValueGetterParams<TaxGridRow>) => {
        return params.data?.total ?? null
      },
      cellRenderer: TaxTotalCurrencyRenderer,
    },
    {
      headerName: 'DEFERRED',
      flex: 0.3,
      sortable: false,
      hide: true,
      cellStyle: { textAlign: 'center' },
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      valueGetter: (params: ValueGetterParams<TaxGridRow>) => {
        return params.data?.non_deferred ?? null
      },
      filterValueGetter: (params: ValueGetterParams<TaxGridRow>) => {
        const nonDeferred = params.data?.non_deferred
        if (nonDeferred === null || nonDeferred === undefined) {
          return 'N/A'
        }
        return nonDeferred ? 'No' : 'Yes'
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<TaxGridRow>) => {
        const nonDeferred = params.data?.non_deferred
        if (nonDeferred === null || nonDeferred === undefined) {
          return 'N/A'
        }
        return nonDeferred ? 'No' : 'Yes'
      },
      cellRenderer: (params: ICellRendererParams<TaxGridRow>) => {
        if (params.value === null || params.value === undefined) {
          return 'N/A'
        }
        return params.value ? 'No' : 'Yes'
      },
    },
    {
      headerName: 'STATE',
      flex: 0.3,
      sortable: false,
      cellStyle: { textAlign: 'center' },
      initialHide: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      valueGetter: (params: ValueGetterParams<TaxGridRow>) => {
        return params.data?.state || ''
      },
    },
  ]
}
